import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { EventCheckinManager } from "@/components/event/EventCheckinManager";
import { FirstTimerFollowupsDialog } from "@/components/admin/first-timer-followups/FirstTimerFollowupsDialog";

const EventCheckin = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { isAdmin } = useIsAdmin();
  const [followupsOpen, setFollowupsOpen] = useState(false);

  // Fetch event details (minimal)
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["checkin-event", id],
    queryFn: async () => {
      if (!id) throw new Error("Event ID required");
      const { data, error } = await supabase
        .from("events")
        .select("id, title, start_time, end_time, venue, capacity, host_id, secondary_host_id, timezone")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Departure waves (empty for ungrouped events)
  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ["checkin-groups", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_groups")
        .select("id, name, start_time, capacity, host_id, secondary_host_id, display_order")
        .eq("event_id", id!)
        .order("start_time", { ascending: true })
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!id,
  });

  // Fetch confirmed tickets with profile info
  const { data: tickets, isLoading: ticketsLoading } = useQuery({
    queryKey: ["checkin-tickets", id],
    queryFn: async () => {
      if (!id) return [];
      const { data, error } = await supabase
        .from("tickets")
        .select(`
          id, quantity, user_id, attendance_status, created_at, group_id, marked_by, marked_at,
          profiles!tickets_user_id_fkey (
            id, first_name, last_name, avatar_url, phone
          ),
          marker:profiles!tickets_marked_by_fkey (
            first_name
          )
        `)
        .eq("event_id", id)
        .eq("status", "confirmed");
      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
    // Two hosts often work the same wave in parallel; a plain 10s poll keeps
    // both screens fresh. Deliberately not Realtime — a dropped websocket on
    // venue wifi fails worse than a poll that just retries.
    refetchInterval: 10000,
  });

  // Fetch event counts per user (for "First Timer" badge)
  const userIds = tickets?.map((t: any) => t.user_id).filter(Boolean) || [];
  const { data: eventCounts } = useQuery({
    queryKey: ["checkin-event-counts", userIds],
    queryFn: async () => {
      if (userIds.length === 0) return {};
      // Count confirmed tickets per user across all events
      const { data, error } = await supabase
        .from("tickets")
        .select("user_id")
        .eq("status", "confirmed")
        .in("user_id", userIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      data?.forEach((t: any) => {
        counts[t.user_id] = (counts[t.user_id] || 0) + 1;
      });
      return counts;
    },
    enabled: userIds.length > 0,
  });

  const isLoading = eventLoading || ticketsLoading || groupsLoading;

  // Access control: host or admin only
  // Wave hosts run their wave, so they count as hosts of the event too.
  const hostedGroups = (groups ?? []).filter(
    (g) => !!userId && (g.host_id === userId || g.secondary_host_id === userId)
  );
  const isHost =
    event?.host_id === userId || event?.secondary_host_id === userId || hostedGroups.length > 0;
  const hasAccess = isHost || isAdmin;

  if (isLoading) return <LoadingSpinner />;

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <p className="text-muted-foreground">Event not found</p>
        <Button variant="ghost" onClick={() => navigate("/events")} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Events
        </Button>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <p className="text-muted-foreground">Only the event host can access check-in</p>
        <Button variant="ghost" onClick={() => navigate(`/events/${id}`)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Event
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-56">
      {/* Compact header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => navigate(`/events/${id}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-semibold truncate">{event.title}</h1>
            <p className="text-xs text-muted-foreground">
              {format(new Date(event.start_time), "EEE, MMM d · h:mm a")} · {event.venue}
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 shrink-0"
              onClick={() => setFollowupsOpen(true)}
              title="Process first-timer follow-ups"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Follow-ups</span>
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-lg mx-auto px-4 py-4">
        <EventCheckinManager
          eventId={event.id}
          tickets={tickets || []}
          capacity={event.capacity}
          eventCounts={eventCounts || {}}
          groups={groups ?? []}
          timezone={event.timezone || "Europe/London"}
          defaultGroupId={hostedGroups.length === 1 ? hostedGroups[0].id : null}
        />
      </div>

      {isAdmin && (
        <FirstTimerFollowupsDialog
          eventId={event.id}
          open={followupsOpen}
          onOpenChange={setFollowupsOpen}
        />
      )}
    </div>
  );
};

export default EventCheckin;
